import React, { useState, useEffect, useCallback } from 'react';
import {
  FiUser,
  FiMail,
  FiPhone,
  FiMapPin,
  FiEdit2,
  FiTrash2,
  FiPlus,
  FiSave,
  FiX,
  FiLock,
  FiLogOut,
  FiCheck,
  FiStar,
  FiHome,
  FiAlertCircle,
  FiEye,
  FiEyeOff,
  FiShield,
  FiNavigation,
  FiDollarSign,
  FiChevronRight,
} from 'react-icons/fi';
import axios from 'axios';
import MapaUbicacion from '../shared/components/MapaUbicacion';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

function PerfilCliente({ sessionId, schemaName, clienteData, config, onLogout, onVerCredito, onPerfilActualizado }) {
  const [perfil, setPerfil] = useState(null);
  const [direcciones, setDirecciones] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);
  const [exito, setExito] = useState(null);

  const [editandoPerfil, setEditandoPerfil] = useState(false);
  const [perfilForm, setPerfilForm] = useState({});

  const [cambiandoClave, setCambiandoClave] = useState(false);
  const [claveForm, setClaveForm] = useState({ actual: '', nueva: '' });
  const [mostrarClaves, setMostrarClaves] = useState(false);

  const [editandoDir, setEditandoDir] = useState(null);
  const [dirForm, setDirForm] = useState({
    nombre: '',
    direccion: '',
    principal: false,
    lat: null,
    lng: null,
  });

  const [mapaDirAbierto, setMapaDirAbierto] = useState(false);
  const [mapaDirCoords, setMapaDirCoords] = useState(null);
  const [mapaDirTexto, setMapaDirTexto] = useState('');
  const [mapaDirError, setMapaDirError] = useState(null);
  const [mapaDirObteniendo, setMapaDirObteniendo] = useState(false);

  const mostrarToast = (msg) => {
    setExito(msg);
    setTimeout(() => setExito(null), 3000);
  };

  const cargarPerfil = useCallback(async () => {
    if (!sessionId) return;
    try {
      const res = await axios.get(`${API_URL}/perfil-cliente/${schemaName}/${sessionId}`);
      if (res.data.status === 'success') {
        setPerfil(res.data.data);
        setDirecciones(res.data.data.direcciones || []);
        setPerfilForm({
          nombre: res.data.data.nombre || '',
          apellido: res.data.data.apellido || '',
          telefono: res.data.data.telefono || '',
          email: res.data.data.email || '',
        });
      }
    } catch (err) {
      setError('Error al cargar el perfil');
    } finally {
      setCargando(false);
    }
  }, [sessionId, schemaName]);

  useEffect(() => {
    cargarPerfil();
  }, [cargarPerfil]);

  const guardarPerfil = async (e) => {
    e.preventDefault();
    setError(null);
    try {
      const res = await axios.put(
        `${API_URL}/perfil-cliente/${schemaName}/${sessionId}`,
        perfilForm
      );
      if (res.data.status === 'success') {
        setPerfil((prev) => ({ ...prev, ...res.data.data }));
        setEditandoPerfil(false);
        mostrarToast('Perfil actualizado');
        if (onPerfilActualizado) onPerfilActualizado(res.data.data);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al guardar');
    }
  };

  const cambiarClave = async (e) => {
    e.preventDefault();
    setError(null);
    if (claveForm.nueva.length < 6) {
      setError('La contraseña debe tener al menos 6 caracteres');
      return;
    }
    try {
      const res = await axios.put(
        `${API_URL}/perfil-cliente/${schemaName}/${sessionId}/cambiar-clave`,
        { clave_actual: claveForm.actual, clave_nueva: claveForm.nueva }
      );
      if (res.data.status === 'success') {
        setCambiandoClave(false);
        setClaveForm({ actual: '', nueva: '' });
        mostrarToast('Contraseña actualizada');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al cambiar contraseña');
    }
  };

  const guardarDireccion = async (e) => {
    e.preventDefault();
    setError(null);
    if (!dirForm.nombre.trim() || !dirForm.direccion.trim()) {
      setError('Nombre y dirección son obligatorios');
      return;
    }
    try {
      if (editandoDir?.id) {
        const res = await axios.put(
          `${API_URL}/perfil-cliente/${schemaName}/${sessionId}/direcciones/${editandoDir.id}`,
          dirForm
        );
        if (res.data.status === 'success') {
          setDirecciones((prev) => prev.map((d) => (d.id === editandoDir.id ? res.data.data : d)));
        }
      } else {
        const res = await axios.post(
          `${API_URL}/perfil-cliente/${schemaName}/${sessionId}/direcciones`,
          dirForm
        );
        if (res.data.status === 'success') {
          setDirecciones((prev) =>
            res.data.data.principal
              ? prev.map((d) => ({ ...d, principal: false })).concat(res.data.data)
              : [...prev, res.data.data]
          );
        }
      }
      setEditandoDir(null);
      setDirForm({ nombre: '', direccion: '', principal: false, lat: null, lng: null });
      mostrarToast(editandoDir?.id ? 'Dirección actualizada' : 'Dirección agregada');
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al guardar dirección');
    }
  };

  const eliminarDireccion = async (id) => {
    try {
      await axios.delete(`${API_URL}/perfil-cliente/${schemaName}/${sessionId}/direcciones/${id}`);
      setDirecciones((prev) => prev.filter((d) => d.id !== id));
      mostrarToast('Dirección eliminada');
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al eliminar');
    }
  };

  const toggleFavorita = async (dir) => {
    try {
      const nuevoPrincipal = !dir.principal;
      const res = await axios.put(
        `${API_URL}/perfil-cliente/${schemaName}/${sessionId}/direcciones/${dir.id}`,
        { ...dir, principal: nuevoPrincipal }
      );
      if (res.data.status === 'success') {
        setDirecciones((prev) => {
          const updated = prev.map((d) =>
            d.id === dir.id
              ? { ...d, principal: nuevoPrincipal }
              : nuevoPrincipal
                ? { ...d, principal: false }
                : d
          );
          updated.sort((a, b) => (b.principal ? 1 : 0) - (a.principal ? 1 : 0) || a.id - b.id);
          return updated;
        });
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al actualizar');
    }
  };

  const usarGPSNuevaDir = () => {
    if (!navigator.geolocation) {
      setMapaDirError('Tu navegador no soporta geolocalizacion.');
      return;
    }
    setMapaDirObteniendo(true);
    setMapaDirError(null);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setMapaDirObteniendo(false);
        setMapaDirCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        setMapaDirAbierto(true);
      },
      (err) => {
        setMapaDirObteniendo(false);
        if (err.code === 1) setMapaDirError('Permiso de ubicación denegado.');
        else setMapaDirError('No se pudo obtener tu ubicación.');
        setMapaDirAbierto(true);
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
    );
  };

  const abrirMapaNuevaDir = () => {
    setMapaDirAbierto(true);
    setMapaDirError(null);
    setMapaDirTexto(dirForm.direccion || '');
    if (dirForm.lat && dirForm.lng) {
      setMapaDirCoords({ lat: dirForm.lat, lng: dirForm.lng });
    } else {
      setMapaDirCoords(null);
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (pos) => setMapaDirCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
          (err) => {
            if (err.code !== 1) setMapaDirError('No se pudo obtener tu ubicacion.');
          },
          { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
        );
      }
    }
  };

  const confirmarUbicacionNuevaDir = () => {
    if (mapaDirCoords) {
      setDirForm((d) => ({
        ...d,
        direccion: mapaDirTexto || 'Ubicacion seleccionada',
        lat: mapaDirCoords.lat,
        lng: mapaDirCoords.lng,
      }));
    }
    setMapaDirAbierto(false);
    setMapaDirCoords(null);
    setMapaDirTexto('');
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API_URL}/auth/logout-cliente`, {
        session_id: sessionId,
        schema_name: schemaName,
      });
    } catch {}
    localStorage.removeItem(`agorax_session_${schemaName.replace(/_/g, '-')}`);
    if (onLogout) onLogout();
  };

  if (cargando) {
    return (
      <div style={S.loadingWrap}>
        <div style={S.spinner} />
        <span style={S.loadingText}>Cargando perfil...</span>
      </div>
    );
  }

  return (
    <div style={S.wrapper}>
      {exito && (
        <div style={S.toast}>
          <FiCheck size={16} />
          {exito}
        </div>
      )}

      <div style={S.content}>
        {/* Cabecera del perfil */}
        <div style={S.headerCard}>
          <div style={S.avatar}>{(perfil?.nombre || '?').charAt(0).toUpperCase()}</div>
          <div style={S.headerInfo}>
            <h2 style={S.headerName}>
              {[perfil?.nombre, perfil?.apellido].filter(Boolean).join(' ') || 'Cliente'}
            </h2>
            <p style={S.headerMeta}>
              {perfil?.tipo === 'juridico' ? 'Persona Jurídica' : 'Persona Natural'}
              {perfil?.rif ? ` · RIF: ${perfil.rif}` : ''}
            </p>
          </div>
          <button
            onClick={() => {
              setEditandoPerfil(true);
              setError(null);
            }}
            style={S.iconBtn}
            title="Editar perfil"
          >
            <FiEdit2 size={17} />
          </button>
        </div>

        {error && (
          <div style={S.errorBanner}>
            <FiAlertCircle size={16} />
            {error}
          </div>
        )}

        {/* Seccion: Crédito */}
        {config?.credito_habilitado && onVerCredito && (
          <button
            onClick={onVerCredito}
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              gap: '14px',
              padding: '16px',
              borderRadius: '14px',
              border: '1px solid var(--color-border)',
              background: 'linear-gradient(135deg, var(--color-primary), var(--color-secondary))',
              color: '#fff',
              cursor: 'pointer',
              fontFamily: 'inherit',
              textAlign: 'left',
              boxShadow: '0 4px 16px rgba(11,26,74,0.16)',
              marginBottom: '16px',
            }}
          >
            <div
              style={{
                width: '40px',
                height: '40px',
                borderRadius: '12px',
                background: 'rgba(255,255,255,0.18)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <FiDollarSign size={20} />
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: '15px', fontWeight: 700 }}>Mi crédito</div>
              <div style={{ fontSize: '12px', opacity: 0.85 }}>
                Consulta tu saldo, consumos y pagos
              </div>
            </div>
            <FiChevronRight size={18} style={{ opacity: 0.7 }} />
          </button>
        )}

        {/* Seccion: Datos personales */}
        {editandoPerfil ? (
          <form onSubmit={guardarPerfil} style={S.card}>
            <h3 style={S.cardTitle}>Editar perfil</h3>
            <div style={S.formGrid}>
              <label style={S.fieldLabel}>
                Nombre
                <input
                  style={S.fieldInput}
                  value={perfilForm.nombre}
                  onChange={(e) => setPerfilForm((p) => ({ ...p, nombre: e.target.value }))}
                />
              </label>
              <label style={S.fieldLabel}>
                Apellido
                <input
                  style={S.fieldInput}
                  value={perfilForm.apellido}
                  onChange={(e) => setPerfilForm((p) => ({ ...p, apellido: e.target.value }))}
                />
              </label>
              <label style={S.fieldLabel}>
                Teléfono
                <input
                  style={S.fieldInput}
                  value={perfilForm.telefono}
                  onChange={(e) =>
                    setPerfilForm((p) => ({
                      ...p,
                      telefono: e.target.value.replace(/\D/g, '').slice(0, 15),
                    }))
                  }
                />
              </label>
              <label style={S.fieldLabel}>
                Email
                <input
                  style={S.fieldInput}
                  type="email"
                  value={perfilForm.email}
                  onChange={(e) => setPerfilForm((p) => ({ ...p, email: e.target.value }))}
                />
              </label>
              <div style={S.readonlyField}>
                <span style={S.readonlyLabel}>
                  {perfil?.tipo === 'juridico' ? 'RIF' : 'Cedula'}
                </span>
                <span style={S.readonlyValue}>
                  {perfil?.tipo === 'juridico' ? perfil?.rif || '—' : perfil?.cedula || '—'}
                </span>
                <span style={S.readonlyHint}>No se puede modificar</span>
              </div>
              <div style={S.readonlyField}>
                <span style={S.readonlyLabel}>Tipo de cliente</span>
                <span style={S.readonlyValue}>
                  {perfil?.tipo === 'juridico' ? 'Persona Jurídica' : 'Persona Natural'}
                </span>
                <span style={S.readonlyHint}>No se puede modificar</span>
              </div>
            </div>
            <div style={S.formActions}>
              <button type="button" onClick={() => setEditandoPerfil(false)} style={S.btnOutline}>
                <FiX size={16} />
                Cancelar
              </button>
              <button type="submit" style={S.btnPrimary}>
                <FiSave size={16} />
                Guardar
              </button>
            </div>
          </form>
        ) : (
          <div style={S.card}>
            <h3 style={S.cardTitle}>Datos personales</h3>
            <div style={S.infoGrid}>
              <div style={S.infoItem}>
                <FiPhone size={15} color="var(--color-text-muted)" />
                <span style={S.infoLabel}>Teléfono</span>
                <span style={S.infoValue}>{perfil?.telefono || '—'}</span>
              </div>
              <div style={S.infoItem}>
                <FiMail size={15} color="var(--color-text-muted)" />
                <span style={S.infoLabel}>Email</span>
                <span style={S.infoValue}>
                  {perfil?.email || '—'}
                  {perfil?.email_verificado && (
                    <FiCheck
                      size={12}
                      color="var(--color-success)"
                      style={{ marginLeft: 4 }}
                      title="Verificado"
                    />
                  )}
                </span>
              </div>
              <div style={S.infoItem}>
                <FiUser size={15} color="var(--color-text-muted)" />
                <span style={S.infoLabel}>Cédula</span>
                <span style={S.infoValue}>{perfil?.cedula || '—'}</span>
              </div>
            </div>
          </div>
        )}

        {config?.requiere_ubicacion_cliente !== false && (
        <>
        {/* Seccion: Direcciones */}
        <div style={S.card}>
          <div style={S.cardHeader}>
            <h3 style={S.cardTitle}>
              <FiMapPin size={16} /> Direcciones
            </h3>
            <button
              onClick={() => {
                setEditandoDir({});
                setDirForm({ nombre: '', direccion: '', principal: false, lat: null, lng: null });
                setError(null);
              }}
              style={S.btnSmall}
            >
              <FiPlus size={15} />
              Nueva
            </button>
          </div>

          {editandoDir !== null && (
            <form onSubmit={guardarDireccion} style={S.dirForm}>
              <h4 style={S.dirFormTitle}>
                {editandoDir?.id ? 'Editar direccion' : 'Nueva direccion'}
              </h4>
              <input
                style={S.fieldInput}
                placeholder="Nombre (ej: Casa, Oficina...)"
                value={dirForm.nombre}
                onChange={(e) => setDirForm((d) => ({ ...d, nombre: e.target.value }))}
              />
              {dirForm.lat && dirForm.lng && (
                <div style={{ borderRadius: 10, overflow: 'hidden' }}>
                  <MapaUbicacion lat={dirForm.lat} lng={dirForm.lng} readOnly height="120px" />
                </div>
              )}
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  type="button"
                  onClick={usarGPSNuevaDir}
                  disabled={mapaDirObteniendo}
                  style={{
                    flex: 1,
                    padding: '10px 8px',
                    borderRadius: 12,
                    border: '1px solid var(--color-secondary)',
                    backgroundColor: 'transparent',
                    color: 'var(--color-secondary)',
                    fontSize: 12,
                    fontWeight: 600,
                    cursor: 'pointer',
                    fontFamily: 'inherit',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 6,
                  }}
                >
                  <FiNavigation size={14} />
                  {mapaDirObteniendo ? 'Obteniendo...' : 'Mi ubicación'}
                </button>
                <button
                  type="button"
                  onClick={abrirMapaNuevaDir}
                  style={{
                    flex: 1,
                    padding: '10px 8px',
                    borderRadius: 12,
                    border: '1px solid var(--color-border)',
                    backgroundColor: 'transparent',
                    color: 'var(--color-text-secondary)',
                    fontSize: 12,
                    fontWeight: 600,
                    cursor: 'pointer',
                    fontFamily: 'inherit',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 6,
                  }}
                >
                  <FiMapPin size={14} />
                  Mapa
                </button>
              </div>
              {mapaDirError && (
                <div style={{ fontSize: 11, color: 'var(--color-danger)' }}>{mapaDirError}</div>
              )}
              <button
                type="button"
                onClick={() => setDirForm((d) => ({ ...d, principal: !d.principal }))}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  padding: '8px 12px',
                  borderRadius: 12,
                  border: dirForm.principal
                    ? '1.5px solid #f0b400'
                    : '1px solid var(--color-border)',
                  backgroundColor: dirForm.principal ? '#fffdf5' : 'transparent',
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  fontSize: 13,
                  fontWeight: 600,
                  color: dirForm.principal ? '#b08600' : 'var(--color-text-secondary)',
                }}
              >
                <FiStar
                  size={15}
                  color={dirForm.principal ? '#f0b400' : 'var(--color-text-muted)'}
                />
                {dirForm.principal ? 'Favorita' : 'Marcar como favorita'}
              </button>
              <div style={S.formActions}>
                <button type="button" onClick={() => setEditandoDir(null)} style={S.btnOutline}>
                  <FiX size={15} />
                  Cancelar
                </button>
                <button type="submit" style={S.btnPrimary}>
                  <FiSave size={15} />
                  {editandoDir?.id ? 'Actualizar' : 'Agregar'}
                </button>
              </div>
            </form>
          )}

          {direcciones.length === 0 && !editandoDir ? (
            <div style={S.emptyState}>
              <FiMapPin size={28} color="var(--color-text-muted)" />
              <span>No tienes direcciones guardadas</span>
            </div>
          ) : (
            <div style={S.dirList}>
              {direcciones.map((dir) => (
                <div
                  key={dir.id}
                  style={{ ...S.dirItem, ...(dir.principal ? S.dirItemPrincipal : {}) }}
                >
                  <button
                    onClick={() => toggleFavorita(dir)}
                    style={{ ...S.dirIcon, cursor: 'pointer', border: 'none' }}
                    title={dir.principal ? 'Quitar favorita' : 'Marcar como favorita'}
                  >
                    {dir.principal ? (
                      <FiStar size={16} color="#f0b400" />
                    ) : (
                      <FiHome size={16} color="var(--color-text-muted)" />
                    )}
                  </button>
                  <div style={S.dirInfo}>
                    <div style={S.dirName}>{dir.nombre || 'Sin nombre'}</div>
                    <div style={S.dirText}>{dir.direccion}</div>
                    {dir.lat && dir.lng && (
                      <div style={S.dirMapWrap}>
                        <MapaUbicacion lat={dir.lat} lng={dir.lng} readOnly height="120px" />
                      </div>
                    )}
                  </div>
                  <div style={S.dirActions}>
                    <button
                      onClick={() => {
                        setEditandoDir(dir);
                        setDirForm(dir);
                        setError(null);
                      }}
                      style={S.iconBtnSm}
                      title="Editar"
                    >
                      <FiEdit2 size={14} />
                    </button>
                    <button
                      onClick={() => eliminarDireccion(dir.id)}
                      style={{ ...S.iconBtnSm, color: 'var(--color-danger)' }}
                      title="Eliminar"
                    >
                      <FiTrash2 size={14} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        </>
        )}

        {/* Seccion: Seguridad */}
        <div style={S.card}>
          {cambiandoClave ? (
            <form onSubmit={cambiarClave} style={S.formStack}>
              <h3 style={S.cardTitle}>
                <FiLock size={16} /> Cambiar contraseña
              </h3>
              <div style={S.inputGroup}>
                <FiLock size={16} color="var(--color-text-muted)" />
                <input
                  style={S.fieldInput}
                  type={mostrarClaves ? 'text' : 'password'}
                  placeholder="Contraseña actual"
                  value={claveForm.actual}
                  onChange={(e) => setClaveForm((c) => ({ ...c, actual: e.target.value }))}
                />
              </div>
              <div style={S.inputGroup}>
                <FiShield size={16} color="var(--color-text-muted)" />
                <input
                  style={S.fieldInput}
                  type={mostrarClaves ? 'text' : 'password'}
                  placeholder="Nueva contraseña (min. 6 caracteres)"
                  value={claveForm.nueva}
                  onChange={(e) => setClaveForm((c) => ({ ...c, nueva: e.target.value }))}
                />
                <button
                  type="button"
                  onClick={() => setMostrarClaves(!mostrarClaves)}
                  style={S.togglePwd}
                  tabIndex={-1}
                >
                  {mostrarClaves ? <FiEyeOff size={16} /> : <FiEye size={16} />}
                </button>
              </div>
              <div style={S.formActions}>
                <button
                  type="button"
                  onClick={() => {
                    setCambiandoClave(false);
                    setClaveForm({ actual: '', nueva: '' });
                  }}
                  style={S.btnOutline}
                >
                  <FiX size={15} />
                  Cancelar
                </button>
                <button type="submit" style={S.btnPrimary}>
                  <FiSave size={15} />
                  Actualizar contraseña
                </button>
              </div>
            </form>
          ) : (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: 12,
              }}
            >
              <h3
                style={{
                  margin: 0,
                  fontSize: 15,
                  fontWeight: 600,
                  color: 'var(--color-primary)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                }}
              >
                <FiLock size={16} /> Seguridad
              </h3>
              <button
                onClick={() => {
                  setCambiandoClave(true);
                  setError(null);
                }}
                style={S.btnOutline}
              >
                <FiLock size={15} />
                Cambiar contraseña
              </button>
            </div>
          )}
        </div>

        {/* Logout */}
        <button onClick={handleLogout} style={S.btnLogout}>
          <FiLogOut size={16} />
          Cerrar sesión
        </button>

        {mapaDirAbierto && (
          <div
            onClick={() => {
              setMapaDirAbierto(false);
              setMapaDirCoords(null);
            }}
            style={S.mapOverlay}
          >
            <div onClick={(e) => e.stopPropagation()} style={S.mapModal}>
              <div style={S.mapHeader}>
                <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>
                  Selecciona tu ubicación
                </h3>
                <button
                  onClick={() => {
                    setMapaDirAbierto(false);
                    setMapaDirCoords(null);
                  }}
                  style={S.mapClose}
                >
                  <FiX size={20} />
                </button>
              </div>
              {mapaDirCoords ? (
                <>
                  <div style={{ marginBottom: 12 }}>
                    <input
                      placeholder="Describe tu direccion (ej: Av. Principal, Edif. Torre)"
                      value={mapaDirTexto}
                      onChange={(e) => setMapaDirTexto(e.target.value)}
                      style={S.fieldInput}
                    />
                  </div>
                  <MapaUbicacion
                    lat={mapaDirCoords.lat}
                    lng={mapaDirCoords.lng}
                    readOnly={false}
                    onLocationChange={(lat, lng) => setMapaDirCoords({ lat, lng })}
                    height="300px"
                  />
                  <p
                    style={{
                      fontSize: 11,
                      color: 'var(--color-text-secondary)',
                      textAlign: 'center',
                      margin: '8px 0 0',
                    }}
                  >
                    Arrastra el pin a tu ubicación exacta
                  </p>
                  <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                    <button
                      onClick={() => {
                        setMapaDirAbierto(false);
                        setMapaDirCoords(null);
                      }}
                      style={{
                        flex: 1,
                        padding: '10px',
                        borderRadius: 12,
                        border: '1px solid var(--color-border)',
                        backgroundColor: 'var(--color-surface)',
                        color: 'var(--color-text-secondary)',
                        fontSize: 13,
                        fontWeight: 600,
                        cursor: 'pointer',
                        fontFamily: 'inherit',
                      }}
                    >
                      Cancelar
                    </button>
                    <button
                      onClick={confirmarUbicacionNuevaDir}
                      style={{
                        flex: 1,
                        padding: '10px',
                        borderRadius: 12,
                        border: 'none',
                        backgroundColor: 'var(--color-secondary)',
                        color: 'var(--color-surface)',
                        fontSize: 13,
                        fontWeight: 700,
                        cursor: 'pointer',
                        fontFamily: 'inherit',
                      }}
                    >
                      Confirmar ubicación
                    </button>
                  </div>
                </>
              ) : mapaDirError ? (
                <div style={{ textAlign: 'center', padding: '40px 20px' }}>
                  <p style={{ color: 'var(--color-danger)', fontSize: 13, marginBottom: 16 }}>
                    {mapaDirError}
                  </p>
                  <button
                    onClick={abrirMapaNuevaDir}
                    style={{
                      padding: '10px 24px',
                      borderRadius: 10,
                      border: 'none',
                      backgroundColor: 'var(--color-secondary)',
                      color: 'var(--color-surface)',
                      fontSize: 13,
                      fontWeight: 600,
                      cursor: 'pointer',
                    }}
                  >
                    Reintentar
                  </button>
                </div>
              ) : (
                <div style={{ textAlign: 'center', padding: '50px 0' }}>
                  <div style={S.spinner} />
                  <p style={{ marginTop: 16, fontSize: 13, color: 'var(--color-text-secondary)' }}>
                    Obteniendo tu ubicación...
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const S = {
  wrapper: {
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: 'var(--color-bg)',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  },
  content: {
    padding: '20px 16px 32px',
    maxWidth: '600px',
    width: '100%',
    margin: '0 auto',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  loadingWrap: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '12px',
  },
  spinner: {
    width: '32px',
    height: '32px',
    borderRadius: '50%',
    border: '3px solid var(--color-border)',
    borderTopColor: 'var(--color-secondary)',
    animation: 'spin 0.8s linear infinite',
  },
  loadingText: { fontSize: '14px', color: 'var(--color-text-secondary)' },

  toast: {
    position: 'fixed',
    bottom: '20px',
    left: '50%',
    transform: 'translateX(-50%)',
    backgroundColor: 'var(--color-success)',
    color: '#fff',
    padding: '12px 24px',
    borderRadius: '14px',
    fontSize: '14px',
    fontWeight: 600,
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
    zIndex: 1000,
    animation: 'slideUp 0.3s ease',
  },

  headerCard: {
    backgroundColor: 'var(--color-surface)',
    borderRadius: '20px',
    padding: '20px',
    display: 'flex',
    alignItems: 'center',
    gap: '14px',
    boxShadow: '0 2px 16px rgba(11,26,74,0.06)',
  },
  avatar: {
    width: '56px',
    height: '56px',
    borderRadius: '16px',
    backgroundColor: 'var(--color-primary)',
    color: 'var(--color-surface)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '24px',
    fontWeight: 700,
    flexShrink: 0,
  },
  headerInfo: { flex: 1, minWidth: 0 },
  headerName: {
    margin: 0,
    fontSize: '18px',
    fontWeight: 700,
    color: 'var(--color-primary)',
    letterSpacing: '-0.3px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  headerMeta: { margin: '2px 0 0', fontSize: '12px', color: 'var(--color-text-secondary)' },
  iconBtn: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: '8px',
    borderRadius: '10px',
    color: 'var(--color-text-muted)',
    display: 'flex',
    alignItems: 'center',
  },

  errorBanner: {
    fontSize: '13px',
    color: 'var(--color-danger)',
    backgroundColor: '#fef2f2',
    border: '1px solid #fecaca',
    padding: '10px 14px',
    borderRadius: '12px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },

  card: {
    backgroundColor: 'var(--color-surface)',
    borderRadius: '20px',
    padding: '20px',
    boxShadow: '0 2px 16px rgba(11,26,74,0.06)',
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: '8px',
    marginBottom: '12px',
  },
  cardTitle: {
    margin: '0 0 12px',
    fontSize: '15px',
    fontWeight: 600,
    color: 'var(--color-primary)',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },

  infoGrid: { display: 'flex', flexDirection: 'column', gap: '12px' },
  infoItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '10px 12px',
    backgroundColor: 'var(--color-bg)',
    borderRadius: '12px',
  },
  infoLabel: {
    fontSize: '12px',
    color: 'var(--color-text-muted)',
    minWidth: '60px',
    fontWeight: 500,
  },
  infoValue: {
    fontSize: '14px',
    fontWeight: 600,
    color: 'var(--color-text)',
    flex: 1,
    textAlign: 'right',
  },

  formGrid: { display: 'flex', flexDirection: 'column', gap: '10px' },
  fieldLabel: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    fontSize: '12px',
    fontWeight: 600,
    color: 'var(--color-text-secondary)',
  },
  fieldInput: {
    padding: '10px 12px',
    borderRadius: '12px',
    border: '1px solid var(--color-border)',
    fontSize: '14px',
    fontFamily: 'inherit',
    color: 'var(--color-text)',
    backgroundColor: 'var(--color-bg)',
    outline: 'none',
    width: '100%',
    boxSizing: 'border-box',
  },
  fieldSelect: {
    padding: '10px 12px',
    borderRadius: '12px',
    border: '1px solid var(--color-border)',
    fontSize: '14px',
    fontFamily: 'inherit',
    color: 'var(--color-text)',
    backgroundColor: 'var(--color-bg)',
    outline: 'none',
    width: '100%',
    boxSizing: 'border-box',
    cursor: 'pointer',
  },
  inputGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    backgroundColor: 'var(--color-bg)',
    borderRadius: '12px',
    padding: '0 10px',
    border: '1px solid var(--color-border)',
  },
  togglePwd: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: '4px',
    color: 'var(--color-text-muted)',
    display: 'flex',
    alignItems: 'center',
  },
  formActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '8px',
    marginTop: '4px',
  },
  formStack: { display: 'flex', flexDirection: 'column', gap: '10px' },

  btnPrimary: {
    padding: '10px 18px',
    borderRadius: '12px',
    border: 'none',
    backgroundColor: 'var(--color-secondary)',
    color: 'var(--color-surface)',
    fontSize: '13px',
    fontWeight: 700,
    cursor: 'pointer',
    fontFamily: 'inherit',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    transition: 'opacity 0.2s',
  },
  btnOutline: {
    padding: '10px 18px',
    borderRadius: '12px',
    border: '1px solid var(--color-border)',
    backgroundColor: 'var(--color-surface)',
    fontSize: '13px',
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: 'inherit',
    color: 'var(--color-text-secondary)',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  btnSmall: {
    padding: '7px 14px',
    borderRadius: '10px',
    border: '1px solid var(--color-secondary)',
    backgroundColor: 'transparent',
    color: 'var(--color-secondary)',
    fontSize: '12px',
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: 'inherit',
    display: 'flex',
    alignItems: 'center',
    gap: '5px',
  },

  dirForm: {
    backgroundColor: 'var(--color-bg)',
    borderRadius: '14px',
    padding: '14px',
    marginBottom: '12px',
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
  },
  dirFormTitle: {
    margin: 0,
    fontSize: '13px',
    fontWeight: 600,
    color: 'var(--color-text-secondary)',
  },
  checkLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '13px',
    color: 'var(--color-text-secondary)',
    cursor: 'pointer',
  },

  emptyState: {
    padding: '28px 16px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '8px',
    color: 'var(--color-text-muted)',
    fontSize: '13px',
  },
  dirList: { display: 'flex', flexDirection: 'column', gap: '8px' },
  dirItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '12px',
    borderRadius: '14px',
    backgroundColor: 'var(--color-bg)',
    border: '1px solid var(--color-border)',
  },
  dirItemPrincipal: {
    borderColor: '#f0b400',
    backgroundColor: '#fffdf5',
  },
  dirIcon: {
    width: '36px',
    height: '36px',
    borderRadius: '10px',
    backgroundColor: 'var(--color-surface)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  dirInfo: { flex: 1, minWidth: 0 },
  dirName: { fontSize: '13px', fontWeight: 600, color: 'var(--color-text)' },
  dirText: { fontSize: '12px', color: 'var(--color-text-secondary)', marginTop: '2px' },
  dirMapWrap: { marginTop: 8, borderRadius: 8, overflow: 'hidden' },
  dirActions: { display: 'flex', gap: '4px', flexShrink: 0 },
  iconBtnSm: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: '6px',
    borderRadius: '8px',
    color: 'var(--color-text-muted)',
    display: 'flex',
    alignItems: 'center',
  },

  readonlyField: {
    padding: '10px 12px',
    borderRadius: '12px',
    backgroundColor: 'var(--color-bg)',
    border: '1px solid var(--color-border)',
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  readonlyLabel: { fontSize: '12px', fontWeight: 600, color: 'var(--color-text-secondary)' },
  readonlyValue: { fontSize: '14px', fontWeight: 600, color: 'var(--color-text)' },
  readonlyHint: { fontSize: '10px', color: 'var(--color-text-muted)', fontStyle: 'italic' },

  btnLogout: {
    width: '100%',
    padding: '14px',
    borderRadius: '14px',
    border: '1.5px solid var(--color-danger)',
    backgroundColor: 'transparent',
    color: 'var(--color-danger)',
    fontSize: '14px',
    fontWeight: 700,
    cursor: 'pointer',
    fontFamily: 'inherit',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    marginTop: '4px',
    marginBottom: '16px',
  },

  mapOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 2000,
  },
  mapModal: {
    backgroundColor: 'var(--color-surface)',
    borderRadius: '16px',
    padding: '20px',
    maxWidth: '420px',
    width: '90%',
    maxHeight: '90vh',
    overflowY: 'auto',
    boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
  },
  mapHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '14px',
  },
  mapClose: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    color: 'var(--color-text-muted)',
    display: 'flex',
    alignItems: 'center',
    padding: '4px',
    borderRadius: '8px',
  },
};

export default PerfilCliente;
